<template>
    <div class="sms-verification">
        <div
            v-if="stringIsNotEmpty(content.header.value)"
            class="sms-verification__header"
        >
            <zoe-atom-text
                class="sms-verification__header-text"
                :item="content.header.value"
            />
        </div>

        <div class="sms-verification__content">
            <zoe-atom-text
                v-if="stringIsNotEmpty(content.text.value)"
                class="sms-verification__title"
                :item="content.text.value"
            />

            <form
                class="sms-verification__form"
                @submit.prevent="submit"
            >
                <zoe-molecule-form-row
                    :use-validation="true"
                    :pre-valid="valid"
                >
                    <zoe-atom-input
                        mask="smscode"
                        :value="code"
                        :placeholder="content.placeholder.value"
                        :valid="valid"
                        inputmode="numeric"
                        autocomplete="one-time-code"
                        @blur="preValidateField"
                        @click="resetValidation"
                        @focus="resetValidation"
                        @set="inputValue => code = inputValue"
                    />

                    <p
                        class="sms-verification__resend tiny-text"
                        @click="resendSMSCode"
                    >
                        {{ resendTranslations.clickMessage }}
                    </p>
                </zoe-molecule-form-row>

                <button class="button">
                    <zoe-atom-text
                        v-if="!loading"
                        class="label"
                        :item="content.label.value"
                    />

                    <zoe-atom-loading-icon
                        v-else
                    />
                </button>
            </form>
        </div>
    </div>
</template>

<script setup>
import ZoeAtomInput from '@atoms/input/v1.0.0/zoe-atom-input.vue';
import { ref, watch, onMounted, computed } from 'vue';
import ZoeAtomText from '@atoms/text/v1.0.0/zoe-atom-text.vue';
import { stringIsNotEmpty } from '../../../../../resources/frontend/js/mixins/get-page-data';
import ZoeMoleculeFormRow from '@molecules/form-row/v1.0.0/zoe-molecule-form-row.vue';
import { sbPopup } from '../../../js/vue-flows/default-components/sb-popup/v1/sb-popup-1.1.0';
import { useStore } from 'vuex';
import ZoeAtomLoadingIcon from '@atoms/loading-icon/zoe-atom-loading-icon.vue';
import { smsVerificationTranslation } from '../../../../../resources/frontend/js/mixins/translations';
import { submitForm } from '@form/utils/submit';
import dataLayerPush from '../../../mixins/utils/data-layer-push';
import scrollToSelector from '../../../mixins/utils/scroll-to-selector';
import convertSize from '../../../mixins/utils/convert-size';

const props = defineProps({
    content: {
        type: Object,
        required: true,
    },

    hash: {
        type: String,
        required: true,
    },

    phone: {
        type: String,
        required: true,
    },
});

const store = useStore();

const code = ref('');
const valid = ref(null);
const loading = ref(false);
const resendAmount = ref(0);
const resendTimeout = ref(false);
const resendTranslations = ref({
    clickMessage: smsVerificationTranslation('resend_click_message') || 'Didn\'t receive a code? Click here to resend',
    maxAmountReached: smsVerificationTranslation('resend_max_amount_reached') || 'You\'ve reached to max resend amount',
    timeout: smsVerificationTranslation('resend_timeout') || 'You can only resend a code every 30 seconds',
    confirmation: smsVerificationTranslation('resent_confirmation') || 'Verification code has been resent',
});

const headerBackground = computed(() => props.content.style?.headerBackground?.value || '#C1C5CE');
const resendTextColor = computed(() => props.content.style?.resendTextColor?.value || '#000000');

watch(resendTimeout, (newValue, oldValue) => {
    if (newValue) {
        setTimeout(() => {
            resendTimeout.value = false;
        }, 30000);
    }
});

onMounted(() => {
    dataLayerPush('SMS verification');

    store.commit('page/setVariable', { key: 'smsVerification', value: true });
    store.commit('page/increasePixelStep', { label: 'SMS verification' });

    if (store.state.page.totalSteps === 1) {
        const offset = convertSize(-40, true, true);
        scrollToSelector('.sms-verification', offset);
    } else {
        scrollToSelector('body');
    }
});

const submit = () => {
    preValidateField();

    if (!valid.value) {
        sbPopup(store.state.page.notificationText, 'error');
        return;
    }

    const formData = new FormData();

    formData.append('code', code.value);
    formData.append('phone', props.phone);
    formData.append('hash', props.hash);

    if (store.getters['form/getFieldData']('value', 'eventId')) {
        formData.append('partial_lead_id', store.getters['form/getFieldData']('value', 'eventId'));
    }

    loading.value = true;

    fetch(`${window.location.pathname}/verify-code`, {
        method: 'post',
        body: formData,
    })
        .then(response => response.json())
        .then(data => {
            if (!data.phone_number_verified) {
                valid.value = false;
                sbPopup(store.state.page.notificationText, 'error');
                loading.value = false;
                return;
            }

            submitForm();
        });
};

const resendSMSCode = () => {
    if (resendAmount.value === 5) {
        sbPopup(resendTranslations.value.maxAmountReached, 'error');
        return;
    }

    if (resendTimeout.value) {
        sbPopup(resendTranslations.value.timeout.replace(':seconds', 30), 'error');
        return;
    }

    resendAmount.value++;
    resendTimeout.value = true;

    const formData = new FormData();
    formData.append('p', props.phone);

    if (store.getters['form/getFieldData']('value', 'eventId')) {
        formData.append('partial_lead_id', store.getters['form/getFieldData']('value', 'eventId'));
    }

    fetch(`${window.location.pathname}/resend-verification`, {
        method: 'post',
        body: formData,
    })
        .then(response => {
            if (response.ok) {
                sbPopup(resendTranslations.value.confirmation, 'info');
            }
        });
};

const resetValidation = () => {
    valid.value = null;
};

const preValidateField = () => {
    valid.value = code.value.length === 6;
};
</script>

<style lang="scss" scoped>
@use 'zoe-organism-sms-verification';

.sms-verification {
    --header-background: v-bind(headerBackground);
    --resend-text-color: v-bind(resendTextColor);
}
</style>
